/**
 * Saudi IBAN validation — SA + 22 digits (24 characters total), mod-97 checksum.
 */

export const SAUDI_IBAN_LENGTH = 24;
export const SAUDI_IBAN_REGEX = /^SA\d{22}$/i;
export const SAUDI_IBAN_PLACEHOLDER = 'SA80 1234 5678 9012 3456 7890';
export const SAUDI_IBAN_EXAMPLE = 'SA8012345678901234567890';

/** Strip spaces and uppercase IBAN input */
export function normalizeSaudiIban(value) {
  return String(value || '').replace(/\s/g, '').toUpperCase();
}

/** Display IBAN in groups of 4: SA80 1234 5678 ... */
export function formatSaudiIbanDisplay(iban) {
  const clean = normalizeSaudiIban(iban);
  if (!clean) return '';
  return clean.replace(/(.{4})/g, '$1 ').trim();
}

/** ISO 13616 mod-97 checksum (1 = valid). */
export function isIbanChecksumValid(iban) {
  const clean = normalizeSaudiIban(iban);
  const rearranged = clean.slice(4) + clean.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (ch) => String(ch.charCodeAt(0) - 55));
  let remainder = 0;
  for (let i = 0; i < numeric.length; i += 7) {
    remainder = Number(String(remainder) + numeric.slice(i, i + 7)) % 97;
  }
  return remainder === 1;
}

/**
 * Validate Saudi IBAN format and checksum.
 * @returns {{ valid: boolean, iban?: string, message?: string }}
 */
export function validateSaudiIban(value, messages = {}) {
  const msg = {
    required: messages.required || 'رقم الآيبان مطلوب',
    prefix: messages.prefix || 'يجب أن يبدأ الآيبان السعودي بـ SA',
    length: messages.length || 'يجب أن يتكون الآيبان من 24 حرفاً (SA + 22 رقماً)',
    format: messages.format || 'صيغة الآيبان: SA متبوعة بـ 22 رقماً',
    checksum: messages.checksum || 'رقم التحقق (checksum) للآيبان غير صحيح',
  };

  const clean = normalizeSaudiIban(value);
  if (!clean) return { valid: false, message: msg.required };
  if (!/^SA/i.test(clean)) return { valid: false, message: msg.prefix };
  if (clean.length !== SAUDI_IBAN_LENGTH) return { valid: false, message: msg.length };
  if (!SAUDI_IBAN_REGEX.test(clean)) return { valid: false, message: msg.format };
  if (!isIbanChecksumValid(clean)) return { valid: false, message: msg.checksum };
  return { valid: true, iban: clean };
}

/** Boolean convenience wrapper */
export function isValidSaudiIban(value) {
  return validateSaudiIban(value).valid;
}

/** @deprecated Use validateSaudiIban — legacy alias for banque module */
export const validateIban = validateSaudiIban;
